export const VIEWPORT_FOLLOWER_FRAME_COORDINATOR_MARKER = 'KAIROS_VIEWPORT_FOLLOWER_FRAME_COORDINATOR';

const copyCommand = command => (command ? { ...command } : null);

export const createViewportFollowerFrameCoordinator = ({
  requestFrame,
  cancelFrame,
  onDispatch,
} = {}) => {
  if (typeof requestFrame !== 'function' || typeof cancelFrame !== 'function' || typeof onDispatch !== 'function') {
    throw new TypeError('viewport follower frame coordinator requires scheduling and dispatch callbacks');
  }

  let frameId = null;
  let latest = null;
  let latestVersion = 0;
  let dispatchedVersion = 0;
  let inFlight = false;
  let activeCommand = null;
  let activeComplete = null;
  let epoch = 0;
  let frameToken = null;

  const cancelScheduledFrame = () => {
    const canceledFrameId = frameId;
    frameId = null;
    // Revoke callback identity before asking the host scheduler to cancel. A
    // retained or racing callback must not clear/dispatch a newer frame.
    frameToken = null;
    if (canceledFrameId != null) cancelFrame(canceledFrameId);
  };

  let queueLatest;
  const dispatchLatest = scheduledEpoch => {
    if (scheduledEpoch !== epoch || inFlight || !latest || latestVersion <= dispatchedVersion) return false;
    const command = copyCommand(latest);
    const commandVersion = latestVersion;
    dispatchedVersion = commandVersion;
    inFlight = true;
    let completed = false;
    const complete = () => {
      if (completed) return;
      completed = true;
      if (scheduledEpoch !== epoch) return;
      if (activeComplete !== complete) return;
      activeCommand = null;
      activeComplete = null;
      inFlight = false;
      queueLatest();
    };
    activeCommand = command;
    activeComplete = complete;
    try {
      onDispatch(command, complete);
    } catch (_) {
      complete();
    }
    return true;
  };

  queueLatest = () => {
    if (frameToken != null || inFlight || !latest || latestVersion <= dispatchedVersion) return;
    const scheduledEpoch = epoch;
    const scheduledFrameToken = {};
    frameToken = scheduledFrameToken;
    const scheduledFrameId = requestFrame(() => {
      if (frameToken !== scheduledFrameToken) return;
      frameToken = null;
      frameId = null;
      dispatchLatest(scheduledEpoch);
    });
    if (frameToken === scheduledFrameToken) frameId = scheduledFrameId;
    else cancelFrame(scheduledFrameId);
  };

  return {
    schedule(command, { immediate = false } = {}) {
      if (!command || typeof command !== 'object') return false;
      latest = copyCommand(command);
      latestVersion += 1;
      if (inFlight && activeCommand?.preemptible === true) activeComplete?.();
      if (immediate && !inFlight) {
        // Accepted LINK authority is already authenticated by source pixel
        // settlement and Viewport generation fences. Start its exact visible
        // transport synchronously so a newer intent cannot consume an otherwise
        // avoidable animation-frame delay. Callers leave triangulation and retry
        // work on the historical frame-coalesced path.
        cancelScheduledFrame();
        dispatchLatest(epoch);
        return true;
      }
      queueLatest();
      return true;
    },
    cancel() {
      epoch += 1;
      cancelScheduledFrame();
      latest = null;
      dispatchedVersion = latestVersion;
      activeCommand = null;
      activeComplete = null;
      inFlight = false;
    },
    cancelWhere(predicate) {
      if (typeof predicate !== 'function') return false;
      const cancelLatest = Boolean(latest && predicate(copyCommand(latest)));
      const cancelActive = Boolean(inFlight && activeCommand && predicate(copyCommand(activeCommand)));
      if (!cancelLatest && !cancelActive) return false;
      if (cancelLatest) {
        latest = null;
        dispatchedVersion = latestVersion;
        cancelScheduledFrame();
      }
      if (cancelActive) activeComplete?.();
      return true;
    },
    snapshot() {
      return {
        scheduled: frameToken != null,
        inFlight,
        active: copyCommand(activeCommand),
        latest: copyCommand(latest),
      };
    },
  };
};
